/**
 * AudioManager - Background music playback and volume management
 * Uses HTML5 <audio> element for streaming (no upfront download).
 * Registered as Services.AUDIO in ServiceLocator.
 */

export class AudioManager {
  private masterVolume: number;
  private musicVolume: number;
  private audio: HTMLAudioElement | null = null;

  constructor() {
    this.masterVolume = this.loadFloat('crimsonmandate_master_volume', 0.8);
    this.musicVolume = this.loadFloat('crimsonmandate_music_volume', 0.7);
  }

  /**
   * Start streaming background music. Safe to call multiple times —
   * subsequent calls are ignored if music is already playing.
   * Handles browser autoplay restrictions by retrying on first user interaction.
   */
  playMusic(): void {
    if (this.audio) return;

    this.audio = new Audio('assets/audio/mercurius.mp3');
    this.audio.loop = true;
    this.audio.volume = this.effectiveVolume();

    const tryPlay = () => {
      this.audio!.play().catch(() => {
        // Browser blocked autoplay — retry on first user interaction
        const resume = () => {
          this.audio?.play();
          document.removeEventListener('pointerdown', resume);
          document.removeEventListener('keydown', resume);
        };
        document.addEventListener('pointerdown', resume, { once: true });
        document.addEventListener('keydown', resume, { once: true });
      });
    };

    tryPlay();
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  setMasterVolume(v: number): void {
    this.masterVolume = Phaser.Math.Clamp(v, 0, 1);
    localStorage.setItem('crimsonmandate_master_volume', String(this.masterVolume));
    this.applyVolume();
  }

  setMusicVolume(v: number): void {
    this.musicVolume = Phaser.Math.Clamp(v, 0, 1);
    localStorage.setItem('crimsonmandate_music_volume', String(this.musicVolume));
    this.applyVolume();
  }

  private effectiveVolume(): number {
    return this.masterVolume * this.musicVolume;
  }

  private applyVolume(): void {
    if (this.audio) {
      this.audio.volume = this.effectiveVolume();
    }
  }

  private loadFloat(key: string, fallback: number): number {
    const stored = localStorage.getItem(key);
    if (stored === null) return fallback;
    const parsed = parseFloat(stored);
    return isNaN(parsed) ? fallback : Phaser.Math.Clamp(parsed, 0, 1);
  }
}
